import { type ComposedForm, useFormSelector } from 'lib-node-question-builder';
import { useMemo } from 'react';

import { READONLY_CONDITION } from '../../common/utils/utils';
import type { Income1003FormValues } from './Income1003Form';
import {
  employmentIncomeTypeOptions,
  nonEmploymentIncomeTypeOptions,
} from './income1003Constants';

type IncomeTypeFieldArrayProps = {
  form: ComposedForm<Income1003FormValues, { isWriteAccessDisabled: boolean }>;
  newAppendObject: Income1003FormValues['employmentDetails'][number];
};

export default function IncomeTypeFieldArray({
  form,
  newAppendObject,
}: Readonly<IncomeTypeFieldArrayProps>) {
  const incomeSource = useFormSelector((s) => s.values.incomeSource as string);

  const incomeTypeOptions = useMemo(() => {
    if (incomeSource === 'Employment') {
      return employmentIncomeTypeOptions.map((option) => ({
        label: option.type,
        value: option.typeCode,
      }));
    }

    return nonEmploymentIncomeTypeOptions.map((option) => ({
      label: option.type,
      value: option.typeCode,
    }));
  }, [incomeSource]);

  const isCurrentEmployment = useFormSelector(
    (s) => s.values.currentEmployment === true,
  );

  const shouldHideAddButton = useFormSelector(
    (s) =>
      s.values.incomeSource === 'Non-Employment' &&
      Array.isArray(s.values.employmentDetails) &&
      s.values.employmentDetails.length > 0,
  );

  if (!isCurrentEmployment) {
    return null;
  }

  return (
    <form.FieldArray
      name="employmentDetails"
      label=""
      enableAdd={
        shouldHideAddButton
          ? false
          : {
              when: 'isWriteAccessDisabled',
              isNot: true,
              dataSource: 'screenData',
            }
      }
      addButtonLabel="Add Income Type"
      appendDefaultItem={newAppendObject}
      template={[
        {
          elementType: 'component',
          type: 'grid',
          name: 'employmentDetailsGridRow',
          style: { padding: '0 0.5rem' },
          columns: 13,
          fields: [
            {
              elementType: 'question',
              type: 'select',
              name: 'employmentDetails[{i}].incomeType',
              label: 'Income Type',
              rules: { required: true },
              placeholder: 'Select',
              hasBottomBorder: false,
              style: { padding: 0, marginBottom: '.5rem' },
              options: incomeTypeOptions,
              span: 4,
              conditions: {
                isDisabled: READONLY_CONDITION,
              },
            },
            {
              elementType: 'question',
              type: 'currency',
              name: 'employmentDetails[{i}].monthlyNetIncome',
              label: 'Monthly Amount',
              hasBottomBorder: false,
              style: { padding: 0 },
              rules: { required: true },
              span: 4,
              conditions: {
                isDisabled: READONLY_CONDITION,
              },
            },
            {
              elementType: 'question',
              type: 'date',
              name: 'employmentDetails[{i}].incomeVerifiedDate',
              label: 'Doc Date',
              hasBottomBorder: false,
              style: { padding: 0 },
              span: 4,
              conditions: {
                isDisabled: READONLY_CONDITION,
              },
            },
            {
              elementType: 'component',
              type: 'fieldArrayDeleteButton',
              name: 'deleteEmploymentDetailButton',
              value: '{i}',
              conditions: {
                isDisabled: READONLY_CONDITION,
              },
            },
          ],
        },
      ]}
    />
  );
}
